import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Claim, type InvitePreview, type Me, type Member, type Snapshot, type TreeListItem } from './api';
import { create } from 'zustand';
import { useAuth } from './auth';
import { loadCache, saveCache } from './native';
import { syncPrefs } from './prefs';
import { identify, syncConsent } from './analytics';

/** True while what's on screen is the last saved copy because the network is unreachable (mobile). */
export const useOffline = create<{ offline: boolean }>(() => ({ offline: false }));

/**
 * Anything that isn't an answer from our API counts as "couldn't reach it": fetch() rejecting (TypeError, worded
 * differently per WebView), or the Firebase token refresh failing offline (its error code varies by platform).
 */
const isNetworkError = (e: unknown) => !(e instanceof ApiError);

/** Fetches and caches on success; offline, falls back to the cached copy and raises the offline banner. */
async function withOfflineCache<T>(path: string, fetcher: () => Promise<T>): Promise<T> {
  try {
    const value = await fetcher();
    useOffline.setState({ offline: false });
    void saveCache(path, value);
    return value;
  } catch (e) {
    if (isNetworkError(e)) {
      const cached = await loadCache<T>(path);
      if (cached) {
        useOffline.setState({ offline: true });
        return cached;
      }
    }
    throw e;
  }
}

export const keys = {
  me: ['me'] as const,
  trees: ['trees'] as const,
  tree: (id: string) => ['tree', id] as const,
  members: (id: string) => ['members', id] as const,
  history: (id: string) => ['history', id] as const,
  invites: (id: string) => ['invites', id] as const,
};

export const useMe = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      const me = await api<Me>('GET', '/me');
      syncPrefs(me.user);
      // A choice made on this device before the account had one (the banner) is saved to the account.
      const missing = syncConsent(me.user.analyticsConsent);
      if (missing) void api('PATCH', '/me', { analyticsConsent: missing }).catch(() => {});
      identify(me.user.id);
      return me;
    },
    enabled: Boolean(user),
  });
};

export const useTrees = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: keys.trees,
    // Per user, so a different account on the same phone never sees someone else's list.
    queryFn: () => withOfflineCache(`cache/trees-${user?.uid}.json`, () => api<{ trees: TreeListItem[] }>('GET', '/trees').then((r) => r.trees)),
    enabled: Boolean(user),
    // 'online' mode pauses queries while the device reports no network, so the cache fallback would never run.
    networkMode: 'always',
  });
};

/** Snapshot with a 30 s refresh while the tree is open and on window focus (PRD 9). */
export const useTree = (id: string) =>
  useQuery({
    queryKey: keys.tree(id),
    queryFn: () => withOfflineCache(`trees/${id}.json`, () => api<Snapshot>('GET', `/trees/${id}`)),
    networkMode: 'always',
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: (n, e) => (e as { status?: number }).status !== 404 && n < 2,
  });

export const useMembers = (id: string) =>
  useQuery({
    queryKey: keys.members(id),
    queryFn: () => api<{ members: Member[]; claims: Claim[] }>('GET', `/trees/${id}/members`),
  });

export const useInvitePreview = (token: string) =>
  useQuery({ queryKey: ['invite', token], queryFn: () => api<InvitePreview>('GET', `/invites/${token}`), retry: false });

/** Mutation that refreshes the tree snapshot afterwards. */
export function useTreeMutation<TIn, TOut = unknown>(treeId: string, fn: (v: TIn) => Promise<TOut>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree(treeId) }),
  });
}
